import * as React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Stack, Typography, useTheme } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../utils/tableFunctions'
import TableButtonDelete from '../table/TableButtonDelete'
import { RouteConstants } from '../../navigation/navigation-types'
import TableButtonOpen from '../table/TableButtonOpen'
import EnhancedTableHead from '../table/EnhancedTableHead'
import { ProductWithStatisticsDto } from '../../services/api/sharedTypes/Product'
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded'
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded'
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import WatchLaterRoundedIcon from '@mui/icons-material/WatchLaterRounded'
import CustomTableCell from '../common/CustomTableCell'
import { getFormattedTimeSpanFromSeconds, getFormattedTimeSpanToReadableText, getNumberRoundedInFormattedString, getSecondsFromTimeSpan } from '../../utils/global-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'Jméno',
    },
    {
        id: 'netProfitPerPeriod',
        numeric: true,
        disablePadding: false,
        label: 'Čistý zisk za období',
        component: <CreditCardRoundedIcon />,
        centered: true,
    },
    {
        id: 'netProfitPerHour',
        numeric: true,
        disablePadding: false,
        label: 'Čistý zisk za hodinu',
        component: <HourglassEmptyRoundedIcon />,
        centered: true,
    },
    {
        id: 'netProfitPerEvent',
        numeric: true,
        disablePadding: false,
        label: 'Čistý zisk za akci',
        component: <AttachMoneyRoundedIcon />,
        centered: true,
    },
    {
        id: 'assignedQuantity',
        numeric: true,
        disablePadding: false,
        label: 'Použito',
        component: <EventRoundedIcon />,
        centered: true,
    },
    {
        id: 'operatingTime',
        numeric: false,
        disablePadding: false,
        label: 'Doba provozu',
        component: <WatchLaterRoundedIcon />,
        centered: true,
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]
interface ProductTableProps {
    products: ProductWithStatisticsDto[]
    onDelete: (id: string) => void
    title: string
}

const ProductTable: React.FC<ProductTableProps> = ({
    products,
    onDelete,
    title,
}: ProductTableProps) => {
    const theme = useTheme()

    const [order, setOrder] = React.useState<Order>('asc')
    const [orderBy, setOrderBy] = React.useState<keyof ProductWithStatisticsDto>('name')

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property as keyof ProductWithStatisticsDto)
    }

    return (
        <>
            <Stack justifyContent='space-between' direction='row' mt={3} mb={3}>
                <Typography variant='h6' color='primary'>
                    {title}
                </Typography>
                <Typography mt={theme.spacing(1)} variant='caption'>
                    {products.length} záznamů
                </Typography>
            </Stack>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table size='small'>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={products.length}
                            headCells={headCells}
                        />
                        <TableBody>
                            {stableSort(
                                products.map((row) => {
                                    return {
                                        ...row,
                                        canBeAssigned: row.canBeAssigned ? 1 : 0,
                                        netProfitPerPeriod: row.statistics.netProfitPerPeriod,
                                        netProfitPerHour: row.statistics.netProfitPerHour,
                                        netProfitPerEvent: Math.round(isNaN(row.statistics.netProfitPerEvent) ? 0 : row.statistics.netProfitPerEvent),
                                        costs: row.statistics.costs,
                                        operatingTime: getSecondsFromTimeSpan(row.statistics.operatingTime),
                                        statistics: '',
                                        assignedQuantity: row.statistics.assignedQuantity,
                                        createdAt: row.createdAt ? row.createdAt.toLocaleString() : '',
                                        lastModifiedAt: row.lastModifiedAt ? row.lastModifiedAt.toLocaleString() : '',
                                        assignedEvents: row.assignedEvents ? row.assignedEvents.length : 0,
                                        note: row.note || '',
                                    }
                                }),
                                getComparator(order, orderBy)
                            ).map((row, index: number) => {
                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={index}
                                        style={{
                                            maxHeight: '42px',
                                        }}
                                    >
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.product + '/' + row.id}
                                        >
                                            {row.name}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer', minWidth: 110 }}
                                            route={RouteConstants.product + '/' + row.id}
                                        >
                                            {getNumberRoundedInFormattedString(row.netProfitPerPeriod)}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer', minWidth: 85 }}
                                            route={RouteConstants.product + '/' + row.id}
                                        >
                                            {getNumberRoundedInFormattedString(isNaN(row.netProfitPerHour) ? 0 : row.netProfitPerHour)}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer', minWidth: 85 }}
                                            route={RouteConstants.product + '/' + row.id}
                                        >
                                            {getNumberRoundedInFormattedString(row.netProfitPerEvent)}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.product + '/' + row.id}
                                        >
                                            {row.assignedQuantity}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer', minWidth: 95 }}
                                            route={RouteConstants.product + '/' + row.id}
                                        >
                                            {getFormattedTimeSpanToReadableText(getFormattedTimeSpanFromSeconds(row.operatingTime))}
                                        </CustomTableCell>
                                        <CustomTableCell align='right'>
                                            <Stack spacing={2} direction='row' justifyContent='end'>
                                                <TableButtonOpen route={RouteConstants.product} id={row.id} />
                                                <TableButtonDelete
                                                    nameOfEntityInDialogMessage={row.name}
                                                    onClick={() => onDelete(row.id)}
                                                />
                                            </Stack>
                                        </CustomTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    )
}

export default ProductTable
